import { Component, Host, computed, effect, isBoolean, isString, setAttribute, signal, useState } from "./vidstack-D9mvYVIz.js";
import { watchCueTextChange } from "./vidstack-B1Z6f96t.js";
import { useMediaContext } from "./vidstack-CYE89tBD.js";
import { cloneTemplate, cloneTemplateContent, createTemplate, isHTMLElement, requestScopedAnimationFrame } from "./vidstack-CgfrU1hS.js";
import { MenuPortal, Slider, sliderState } from "./vidstack-D0Mmfcu7.js";
import { AudioGainRadioGroup, AudioGainSlider, Captions, ChaptersRadioGroup, Controls, ControlsGroup, Gesture, GoogleCastButton, MediaAnnouncer, QualitySlider, RadioGroup, SliderChapters, SliderVideo, SpeedSlider, ToggleButton, Tooltip, TooltipContent, TooltipTrigger } from "./vidstack-CCGISNeA.js";
import { renderMenuItemsTemplate } from "./vidstack-DWkX3Edw.js";
import { LitElement } from "./vidstack-DM8v4Khu.js";
import { html } from "lit-html";
//#region src/elements/define/captions-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/captions}
* @example
* ```html
* <media-captions></media-captions>
* ```
*/
var MediaCaptionsElement = class extends Host(HTMLElement, Captions) {
	static tagName = "media-captions";
};
//#endregion
//#region src/elements/define/gesture-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/gesture}
* @example
* ```html
* <media-player>
*   <media-provider>
*     <media-gesture event="pointerup" action="toggle:paused"></media-gesture>
*   </media-provider>
* </media-player>
* ```
*/
var MediaGestureElement = class extends Host(HTMLElement, Gesture) {
	static tagName = "media-gesture";
};
//#endregion
//#region src/elements/define/announcer-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/announcer}
* @example
* ```html
* <media-announcer></media-announcer>
* ```
*/
var MediaAnnouncerElement = class extends Host(HTMLElement, MediaAnnouncer) {
	static tagName = "media-announcer";
};
//#endregion
//#region src/elements/define/controls-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/controls}
* @example
* ```html
* <media-player>
*   <!-- ... -->
*   <media-controls>
*     <media-controls-group></media-controls-group>
*     <media-controls-group></media-controls-group>
*   </media-controls>
* </media-player>
* ```
*/
var MediaControlsElement = class extends Host(HTMLElement, Controls) {
	static tagName = "media-controls";
};
//#endregion
//#region src/elements/define/controls-group-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/controls}
* @example
* ```html
* <media-player>
*   <!-- ... -->
*   <media-controls>
*     <media-controls-group></media-controls-group>
*     <media-controls-group></media-controls-group>
*   </media-controls>
* </media-player>
* ```
*/
var MediaControlsGroupElement = class extends Host(HTMLElement, ControlsGroup) {
	static tagName = "media-controls-group";
};
//#endregion
//#region src/elements/define/title-element.ts
var Title = class extends Component {};
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/title}
* @example
* ```html
* <media-title></media-title>
* ```
*/
var MediaTitleElement = class extends Host(HTMLElement, Title) {
	static tagName = "media-title";
	#media;
	onSetup() {
		this.#media = useMediaContext();
	}
	onConnect() {
		effect(this.#watchTitle.bind(this));
	}
	#watchTitle() {
		const { title } = this.#media.$state;
		this.textContent = title();
	}
};
//#endregion
//#region src/elements/define/chapter-title-element.ts
var ChapterTitle = class extends Component {
	static props = { defaultText: "" };
};
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/chapter-title}
* @example
* ```html
* <media-chapter-title></media-chapter-title>
* ```
*/
var MediaChapterTitleElement = class extends Host(HTMLElement, ChapterTitle) {
	static tagName = "media-chapter-title";
	#media;
	#chapterTitle;
	onSetup() {
		this.#media = useMediaContext();
		this.#chapterTitle = signal("");
	}
	onConnect() {
		const tracks = this.#media.textTracks;
		watchCueTextChange(tracks, "chapters", this.#chapterTitle.set);
		effect(this.#watchChapterTitle.bind(this));
	}
	#watchChapterTitle() {
		const { defaultText } = this.$props;
		this.textContent = this.#chapterTitle() || defaultText();
	}
};
//#endregion
//#region src/elements/define/spinner-element.ts
var Spinner = class extends Component {
	static props = {
		size: 96,
		trackWidth: 8,
		fillPercent: 50
	};
	onConnect(el) {
		requestScopedAnimationFrame(() => {
			if (!this.connectScope) return;
			const root = el.querySelector("svg"), track = root.firstElementChild, trackFill = track.nextElementSibling;
			effect(this.#update.bind(this, root, track, trackFill));
		});
	}
	#update(root, track, trackFill) {
		const { size, trackWidth, fillPercent } = this.$props;
		setAttribute(root, "width", size());
		setAttribute(root, "height", size());
		setAttribute(track, "stroke-width", trackWidth());
		setAttribute(trackFill, "stroke-width", trackWidth());
		setAttribute(trackFill, "stroke-dashoffset", 100 - fillPercent());
	}
};
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/display/buffering-indicator}
* @example
* ```html
* <!-- default values below -->
* <media-spinner size="84" track-width="8" fill-percent="50"></media-spinner>
* ```
* @example
* ```css
* media-spinner [data-part="track"] {
*   color: rgb(255 255 255 / 0.5);
* }
*
* media-spinner [data-part="track-fill"] {
*   color: white;
* }
* ```
*/
var MediaSpinnerElement = class extends Host(LitElement, Spinner) {
	static tagName = "media-spinner";
	render() {
		return html`
      <svg fill="none" viewBox="0 0 120 120" aria-hidden="true" data-part="root">
        <circle cx="60" cy="60" r="54" stroke="currentColor" data-part="track"></circle>
        <circle
          cx="60"
          cy="60"
          r="54"
          stroke="currentColor"
          pathLength="100"
          stroke-dasharray="100"
          data-part="track-fill"
        ></circle>
      </svg>
    `;
	}
};
//#endregion
//#region src/elements/define/layouts/layout-element.ts
var MediaLayout = class extends Component {
	static props = { when: false };
};
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/layouts#custom}
* @example
* ```html
* <media-layout class="video-layout">
*   <template>
*     <!-- ... -->
*   </template>
* </media-layout>
*
* <script>
*   const layout = document.querySelector(".video-layout");
*   // All player state is available.
*   layout.when = ({ viewType }) => viewType === 'video';
* <\/script>
* ```
*/
var MediaLayoutElement = class extends Host(HTMLElement, MediaLayout) {
	static tagName = "media-layout";
	#media;
	onSetup() {
		this.#media = useMediaContext();
	}
	onConnect() {
		effect(this.#watchWhen.bind(this));
	}
	#watchWhen() {
		const root = this.firstElementChild, isTemplate = root?.localName === "template", when = this.$props.when();
		if (!(isBoolean(when) ? when : computed(() => when(this.#media.player.state))())) {
			if (isTemplate) {
				this.textContent = "";
				this.appendChild(root);
			} else if (isHTMLElement(root)) root.style.display = "none";
			return;
		}
		if (isTemplate) this.append(root.content.cloneNode(true));
		else if (isHTMLElement(root)) root.style.display = "";
	}
};
//#endregion
//#region src/elements/define/buttons/google-cast-button-element.ts
/**
* @example
* ```html
* <media-google-cast-button>
*   <media-icon type="chromecast"></media-icon>
* </media-google-cast-button>
* ```
*/
var MediaGoogleCastButtonElement = class extends Host(HTMLElement, GoogleCastButton) {
	static tagName = "media-google-cast-button";
};
//#endregion
//#region src/elements/define/buttons/toggle-button-element.ts
/**
* @example
* ```html
* <media-toggle-button aria-label="...">
*   <!-- ... -->
* </media-toggle-button>
* ```
*/
var MediaToggleButtonElement = class extends Host(HTMLElement, ToggleButton) {
	static tagName = "media-toggle-button";
};
//#endregion
//#region src/elements/define/tooltips/tooltip-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/tooltip}
* @example
* ```html
* <media-tooltip>
*   <media-tooltip-trigger>
*     <media-play-button></media-play-button>
*   </media-tooltip-trigger>
*   <media-tooltip-content placement="top start">
*      <span class="play-tooltip-text">Play</span>
*      <span class="pause-tooltip-text">Pause</span>
*   </media-tooltip-content>
* </media-tooltip>
* ```
*/
var MediaTooltipElement = class extends Host(HTMLElement, Tooltip) {
	static tagName = "media-tooltip";
};
//#endregion
//#region src/elements/define/tooltips/tooltip-trigger-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/tooltip}
* @example
* ```html
* <media-tooltip>
*   <media-tooltip-trigger>
*     <media-play-button></media-play-button>
*   </media-tooltip-trigger>
*   <media-tooltip-content placement="top start">
*      <span class="play-tooltip-text">Play</span>
*      <span class="pause-tooltip-text">Pause</span>
*   </media-tooltip-content>
* </media-tooltip>
* ```
*/
var MediaTooltipTriggerElement = class extends Host(HTMLElement, TooltipTrigger) {
	static tagName = "media-tooltip-trigger";
	onConnect() {
		this.style.display = "contents";
	}
};
//#endregion
//#region src/elements/define/tooltips/tooltip-content-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/tooltip}
* @example
* ```html
* <media-tooltip>
*   <media-tooltip-trigger>
*     <media-play-button></media-play-button>
*   </media-tooltip-trigger>
*   <media-tooltip-content placement="top">
*      <span class="play-tooltip-text">Play</span>
*      <span class="pause-tooltip-text">Pause</span>
*   </media-tooltip-content>
* </media-tooltip>
* ```
*/
var MediaTooltipContentElement = class extends Host(HTMLElement, TooltipContent) {
	static tagName = "media-tooltip-content";
};
//#endregion
//#region src/elements/define/menus/menu-portal-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/menu#portal}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-portal>
*     <media-menu-items></media-menu-items>
*   </media-menu-portal>
* </media-menu>
* ```
*/
var MediaMenuPortalElement = class extends Host(HTMLElement, MenuPortal) {
	static tagName = "media-menu-portal";
	static attrs = { disabled: { converter(value) {
		if (isString(value)) return value;
		return value !== null;
	} } };
};
//#endregion
//#region src/elements/define/menus/chapters-radio-group-element.ts
/**
* @part label - Contains the chapter option title.
* @part start-time - Contains the chapter option start time.
* @part duration - Contains the chapter option duration.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/chapters-radio-group}
* @example
* ```html
* <media-menu>
*   <media-menu-button aria-label="Chapters">
*     <media-icon type="chapters"></media-icon>
*   </media-menu-button>
*   <media-chapters-radio-group thumbnails="...">
*     <template>
*       <media-radio>
*         <media-thumbnail></media-thumbnail>
*         <span data-part="label"></span>
*         <span data-part="start-time"></span>
*         <span data-part="duration"></span>
*       </media-radio>
*     </template>
*    </media-chapters-radio-group>
* </media-menu>
* ```
*/
var MediaChaptersRadioGroupElement = class extends Host(HTMLElement, ChaptersRadioGroup) {
	static tagName = "media-chapters-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this, (el, option) => {
			const { cue, startTime, duration } = option, thumbnailEl = el.querySelector(".vds-thumbnail,media-thumbnail"), startEl = el.querySelector("[data-part=\"start-time\"]"), durationEl = el.querySelector("[data-part=\"duration\"]");
			if (startEl) startEl.textContent = startTime;
			if (durationEl) durationEl.textContent = duration;
			if (thumbnailEl) {
				thumbnailEl.setAttribute("time", cue.startTime + "");
				effect(() => {
					const thumbnails = this.$props.thumbnails();
					if ("src" in thumbnailEl) thumbnailEl.src = thumbnails;
					else if (isString(thumbnails)) thumbnailEl.setAttribute("src", thumbnails);
				});
			}
		});
	}
};
//#endregion
//#region src/elements/define/menus/audio-gain-group-element.ts
/**
* @part label - Contains the audio gain option label.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/audio-gain-group}
* @example
* ```html
* <media-menu>
*   <!-- ... -->
*   <media-menu-items>
*     <media-audio-gain-radio-group>
*       <template>
*         <media-radio>
*           <span data-part="label"></span>
*         </media-radio>
*       </template>
*     </media-audio-gain-radio-group>
*   </media-menu-items>
* </media-menu>
* ```
*/
var MediaAudioGainRadioGroupElement = class extends Host(HTMLElement, AudioGainRadioGroup) {
	static tagName = "media-audio-gain-radio-group";
	onConnect() {
		renderMenuItemsTemplate(this);
	}
};
//#endregion
//#region src/elements/define/menus/radio-group-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/menu/radio-group}
* @example
* ```html
* <media-radio-group value="720">
*   <media-radio value="1080">1080p</media-radio>
*   <media-radio value="720">720p</media-radio>
*   <!-- ... -->
* </media-radio-group>
* ```
*/
var MediaRadioGroupElement = class extends Host(HTMLElement, RadioGroup) {
	static tagName = "media-radio-group";
};
//#endregion
//#region src/elements/define/sliders/slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider}
* @example
* ```html
* <media-slider min="0" max="100" value="50" aria-label="...">
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-slider>
* ```
*/
var MediaSliderElement = class extends Host(HTMLElement, Slider) {
	static tagName = "media-slider";
};
//#endregion
//#region src/elements/define/sliders/slider-video-element.ts
const videoTemplate = /* @__PURE__ */ createTemplate(`<video muted playsinline preload="none" style="max-width: unset;"></video>`);
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider-video}
* @example
* ```html
* <media-time-slider>
*   <media-slider-preview>
*     <media-slider-video src="/low-res-video.mp4"></media-slider-video>
*   </media-slider-preview>
* </media-time-slider>
* ```
*/
var MediaSliderVideoElement = class extends Host(HTMLElement, SliderVideo) {
	static tagName = "media-slider-video";
	#media;
	#video = this.#createVideo();
	onSetup() {
		this.#media = useMediaContext();
		this.$state.video.set(this.#video);
	}
	onConnect() {
		const { canLoad } = this.#media.$state, { src, crossOrigin } = this.$state;
		if (this.#video.parentNode !== this) this.prepend(this.#video);
		effect(() => {
			setAttribute(this.#video, "crossorigin", crossOrigin());
			setAttribute(this.#video, "preload", canLoad() ? "auto" : "none");
			setAttribute(this.#video, "src", src());
		});
	}
	#createVideo() {
		return cloneTemplateContent(videoTemplate);
	}
};
//#endregion
//#region src/elements/define/sliders/audio-gain-slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/audio-gain-slider}
* @example
* ```html
* <media-audio-gain-slider>
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-audio-gain-slider>
* ```
* @example
* ```html
* <media-audio-gain-slider>
*   <!-- ... -->
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   </media-slider-preview>
* </media-audio-gain-slider>
* ```
*/
var MediaAudioGainSliderElement = class extends Host(HTMLElement, AudioGainSlider) {
	static tagName = "media-audio-gain-slider";
};
//#endregion
//#region src/elements/define/sliders/speed-slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/speed-slider}
* @example
* ```html
* <media-speed-slider>
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-speed-slider>
* ```
* @example
* ```html
* <media-speed-slider>
*   <!-- ... -->
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   </media-slider-preview>
* </media-speed-slider>
* ```
*/
var MediaSpeedSliderElement = class extends Host(HTMLElement, SpeedSlider) {
	static tagName = "media-speed-slider";
};
//#endregion
//#region src/elements/define/sliders/quality-slider-element.ts
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/quality-slider}
* @example
* ```html
* <media-quality-slider>
*   <div class="track"></div>
*   <div class="track-fill"></div>
*   <div class="track-progress"></div>
*   <div class="thumb"></div>
* </media-quality-slider>
* ```
* @example
* ```html
* <media-quality-slider>
*   <!-- ... -->
*   <media-slider-preview>
*     <media-slider-value></media-slider-value>
*   </media-slider-preview>
* </media-quality-slider>
* ```
*/
var MediaQualitySliderElement = class extends Host(HTMLElement, QualitySlider) {
	static tagName = "media-quality-slider";
};
//#endregion
//#region src/elements/define/sliders/slider-chapters-element.ts
/**
* @part chapter-title - Contains the current chapter title.
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider-chapters}
* @example
* ```html
* <media-time-slider>
*   <media-slider-chapters>
*     <template>
*       <div class="slider-chapter">
*         <div class="slider-track"></div>
*         <div class="slider-track-fill"></div>
*         <div class="slider-track-progress"></div>
*       </div>
*     </template>
*   </media-slider-chapters>
* </media-time-slider>
* ```
*/
var MediaSliderChaptersElement = class extends Host(HTMLElement, SliderChapters) {
	static tagName = "media-slider-chapters";
	#template = null;
	onConnect() {
		requestScopedAnimationFrame(() => {
			if (!this.connectScope) return;
			const template = this.querySelector("template");
			if (template) {
				this.#template = template;
				effect(this.#renderTemplate.bind(this));
			}
		});
	}
	#renderTemplate() {
		if (!this.#template) return;
		const elements = cloneTemplate(this.#template, this.cues.length || 1);
		this.setRefs(elements);
	}
};
//#endregion
//#region src/elements/define/sliders/slider-steps-element.ts
var SliderSteps = class extends Component {};
/**
* @docs {@link https://www.vidstack.io/docs/wc/player/components/sliders/slider-steps}
* @example
* ```html
* <media-slider>
*   <media-slider-steps>
*     <template>
*       <div class="slider-step"></div>
*     </template>
*   </media-slider-steps>
* </media-slider>
* ```
*/
var MediaSliderStepsElement = class extends Host(HTMLElement, SliderSteps) {
	static tagName = "media-slider-steps";
	#template = null;
	onConnect(el) {
		requestScopedAnimationFrame(() => {
			if (!this.connectScope) return;
			this.#template = el.querySelector("template");
			if (this.#template) effect(this.#render.bind(this));
		});
	}
	#render() {
		if (!this.#template) return;
		const { min, max, step } = useState(sliderState), steps = (max() - min()) / step();
		cloneTemplate(this.#template, Math.floor(steps) + 1);
	}
};
//#endregion
export { MediaAnnouncerElement, MediaAudioGainRadioGroupElement, MediaAudioGainSliderElement, MediaCaptionsElement, MediaChapterTitleElement, MediaChaptersRadioGroupElement, MediaControlsElement, MediaControlsGroupElement, MediaGestureElement, MediaGoogleCastButtonElement, MediaLayoutElement, MediaMenuPortalElement, MediaQualitySliderElement, MediaRadioGroupElement, MediaSliderChaptersElement, MediaSliderElement, MediaSliderStepsElement, MediaSliderVideoElement, MediaSpeedSliderElement, MediaSpinnerElement, MediaTitleElement, MediaToggleButtonElement, MediaTooltipContentElement, MediaTooltipElement, MediaTooltipTriggerElement };
